import { ColumnTable } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationsKeys } from '../../../../locales/translations';
import ActionCell from '../parts/ActionCell';
import DetailTooltipCell from '../parts/DetailTooltipCell';
import { CourseGroupListItem } from '../types';

/**
 * HU 15564 — useColumnsList recibe los callbacks como parámetros separados
 * (no como objeto literal) para que la identidad de las dependencias del
 * `useMemo` interno se mantenga estable entre renders cuando el caller usa
 * `useCallback`. Pasar un objeto literal hubiera defeated el memo.
 */
export function useColumnsList(
  onTooltipDetail: (id: number) => void,
  onEditNavigate: (id: number) => void,
  onDelete: (group: CourseGroupListItem) => void,
): ColumnTable<CourseGroupListItem>[] {
  const prefix = 'list.table';
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);

  return useMemo<ColumnTable<CourseGroupListItem>[]>(
    () => [
      {
        columnName: 'name',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.name`),
        cellFormat: ({ row }) => row.name,
      },
      {
        columnName: 'code',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center text-nowrap',
        headerText: t(`${prefix}.code`),
        cellFormat: ({ row }) => row.code,
      },
      {
        columnName: 'school',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.school`),
        cellFormat: ({ row }) => row.school?.name ?? '-',
      },
      {
        columnName: 'detail',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.itemDetail`),
        cellFormat: ({ row }) => (
          <DetailTooltipCell row={row} onClick={onTooltipDetail} />
        ),
      },
      {
        columnName: 'action',
        thClassName: 'text-center bg-secondary',
        tdClassName: 'text-center',
        headerText: t(`${prefix}.action`),
        cellFormat: ({ row }) => (
          <ActionCell
            row={row}
            onEdit={onEditNavigate}
            onDelete={onDelete}
            editLabel={t(`${prefix}.edit`)}
            deleteLabel={t(`${prefix}.delete`)}
          />
        ),
      },
    ],
    [t, onTooltipDetail, onEditNavigate, onDelete],
  );
}
